import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { HubAttachment } from '@1financial/shared';
import { getAccessToken, useAuth, authenticatedFetch } from '../lib/auth-context.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { fmtDate } from '../lib/date.js';

type FeedItem =
  | {
      kind: 'activity';
      id: string;
      action: string;
      summary: string;
      userId: string | null;
      userName: string | null;
      entityType: string;
      entityId: string | null;
      createdAt: string;
    }
  | {
      kind: 'message';
      id: string;
      authorKind: 'USER' | 'OPPY' | 'SYSTEM';
      authorId: string | null;
      authorName: string;
      body: string;
      attachment: HubAttachment | null;
      createdAt: string;
    };

type UserRow = { id: string; displayName: string; role: string };

const ACTION_ICON: Record<string, string> = {
  CREATE: '✚', UPDATE: '✎', DELETE: '🗑', COMPLETE: '✅', REASSIGN: '👥',
  ARCHIVE: '📦', RESTORE: '♻️', LOGIN: '→', LOGOUT: '←', EXPORT: '⇩', IMPORT: '⇧',
  SENT_BACK: '↩️', APPROVE: '✍️', READY_FOR_REVIEW: '🔍', TIME_LOGGED: '⏱️',
  ATTENDANCE_IN: '🟢', ATTENDANCE_OUT: '🔴', DOCUMENT_UPLOADED: '📎',
  PROPOSAL_SENT: '📨', NOTE_ADDED: '📝', NUDGE: '⏰',
};

function entityLink(item: Extract<FeedItem, { kind: 'activity' }>): string | null {
  if (!item.entityId) return null;
  switch (item.entityType) {
    case 'COMPLIANCE_ITEM': return `/compliance/${item.entityId}`;
    case 'CLIENT': return `/clients/${item.entityId}`;
    case 'ENGAGEMENT': return `/engagements/${item.entityId}`;
    case 'PROPOSAL': return `/proposals/${item.entityId}`;
    default: return null;
  }
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return sameDay ? time : `${fmtDate(d)} ${time}`;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Upload one file/blob to the Hub attachment store; returns meta for message create. */
async function uploadHubFile(file: File | Blob, name: string): Promise<HubAttachment> {
  const fd = new FormData();
  fd.append('file', file, name);
  const res = await authenticatedFetch('/v1/hub/upload', {
    method: 'POST',
    headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    body: fd,
  });
  const body = await res.json() as { ok?: boolean; data?: HubAttachment; error?: { message?: string } };
  if (!res.ok || !body.ok || !body.data) throw new Error(body.error?.message ?? `Upload failed: ${res.statusText}`);
  return body.data;
}

/** Renders a chat attachment. Files are behind auth, so fetch as blob → object URL. */
function AttachmentView({ att }: { att: HubAttachment }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    authenticatedFetch(`/v1/hub/attachments/${att.path}`, {
      headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.blob();
      })
      .then((b) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(b);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [att.path]);

  if (failed) return <p className="text-xs text-rose-400 mt-1">Attachment unavailable</p>;
  if (!url) return <p className="text-xs text-muted mt-1">Loading attachment…</p>;

  if (att.kind === 'image') {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mt-1.5 w-fit">
        <img src={url} alt={att.name} className="max-w-[240px] max-h-48 rounded-lg border border-border object-cover" />
      </a>
    );
  }
  if (att.kind === 'audio') {
    return (
      <div className="mt-1.5">
        <audio controls src={url} className="h-8 w-64 max-w-full" />
        {att.durationSec !== undefined && (
          <p className="text-[10px] text-muted mt-0.5">🎙 {fmtDuration(att.durationSec)}</p>
        )}
      </div>
    );
  }
  return (
    <a
      href={url}
      download={att.name}
      className="mt-1.5 inline-flex items-center gap-2 text-xs text-accent border border-border hover:border-accent rounded-lg px-2.5 py-1.5"
    >
      <span>📎</span>
      <span className="truncate max-w-[180px]">{att.name}</span>
      <span className="text-muted shrink-0">{fmtSize(att.size)}</span>
    </a>
  );
}

// Must mirror the token shape the API matches on (apps/api/src/routes/hub.ts).
const MENTION_SPLIT_RE = /(@[A-Za-z0-9][A-Za-z0-9._-]*)/g;
const MENTION_FULL_RE = /^@[A-Za-z0-9][A-Za-z0-9._-]*$/;

/** Render message text with @mentions highlighted and wizard deep links clickable. */
const ONBOARD_LINK_RE = /(\/clients\?onboard=[0-9a-f-]{36})/g;
const ONBOARD_LINK_FULL_RE = /^\/clients\?onboard=[0-9a-f-]{36}$/;

function renderBody(body: string) {
  return body.split(MENTION_SPLIT_RE).flatMap((part, i) =>
    MENTION_FULL_RE.test(part) ? [
      <span key={i} className="text-accent font-medium">{part}</span>,
    ] : (
      part.split(ONBOARD_LINK_RE).map((sub, j) =>
        ONBOARD_LINK_FULL_RE.test(sub) ? (
          <Link key={`${i}-${j}`} to={sub} className="text-accent hover:underline">{sub}</Link>
        ) : (
          sub
        ),
      )
    ),
  );
}

/** Deterministic insert token: displayName with spaces removed (API rule 2). */
function mentionToken(u: UserRow): string {
  return u.displayName.replace(/\s+/g, '');
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authenticatedFetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}`, ...(init?.headers ?? {}) },
  });
  if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
  return res.json() as Promise<T>;
}

export function HubPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { canDo } = useCapabilities();
  const [kind, setKind] = useState<'all' | 'activity' | 'chat'>('all');
  const [person, setPerson] = useState('');
  const [draft, setDraft] = useState('');
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [asking, setAsking] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [older, setOlder] = useState<FeedItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingAttachment, setPendingAttachment] = useState<HubAttachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [recState, setRecState] = useState<'idle' | 'recording' | 'preview'>('idle');
  const [recSeconds, setRecSeconds] = useState(0);
  const [recPreview, setRecPreview] = useState<{ blob: Blob; durationSec: number; url: string } | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recChunksRef = useRef<Blob[]>([]);
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recStartRef = useRef(0);
  const recCancelRef = useRef(false);

  const params = new URLSearchParams({ limit: '100', kind });
  if (person) params.set('userId', person);

  const feed = useQuery({
    queryKey: ['hub-feed', kind, person],
    queryFn: async () => {
      const body = await apiFetch<{ ok: true; data: { items: FeedItem[] } }>(`/v1/hub/feed?${params}`);
      return body.data.items;
    },
    refetchInterval: 60000, // fallback if SSE drops
    refetchOnWindowFocus: false, // SSE + the 60s fallback keep it fresh; focus storms re-run an expensive feed query
  });

  const users = useQuery({
    // Team uses ['users'] for the full paginated object; Hub caches an active-staff array.
    queryKey: ['users', 'hub-active-staff'],
    queryFn: async () => {
      const body = await apiFetch<{ ok: true; data: { items: UserRow[] } }>('/v1/users?limit=100&status=ACTIVE');
      return body.data.items.filter((u) => u.role !== 'CLIENT');
    },
  });

  // Real-time: SSE pushes each new feed item; invalidate the query on arrival.
  useEffect(() => {
    const ctrl = new AbortController();
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | null = null;

    async function connect() {
      try {
        const res = await authenticatedFetch('/v1/hub/stream', {
          headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          if (res.status === 401 || res.status === 403) stopped = true;
          throw new Error(`stream ${res.status}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx: number;
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const chunk = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            if (chunk.split('\n').some((l) => l.startsWith('data: '))) {
              void queryClient.invalidateQueries({ queryKey: ['hub-feed'] });
            }
          }
        }
      } catch (error) {
        if (ctrl.signal.aborted || (error instanceof Error && error.name === 'AbortError')) stopped = true;
      }
      if (!stopped) retry = setTimeout(() => void connect(), 3000);
    }

    void connect();
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      ctrl.abort();
    };
  }, [queryClient, user?.id, user?.role]);

  const items = [...(feed.data ?? []), ...older].sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1));

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [feed.data, asking]);

  async function loadEarlier() {
    const oldest = items[0];
    if (!oldest) return;
    const p = new URLSearchParams({ limit: '100', kind, cursor: oldest.id });
    if (person) p.set('userId', person);
    const body = await apiFetch<{ ok: true; data: { items: FeedItem[] } }>(`/v1/hub/feed?${p}`);
    setOlder((prev) => [...prev, ...body.data.items]);
  }

  const clearChat = useMutation({
    mutationFn: async () => {
      await apiFetch('/v1/hub/messages', { method: 'DELETE' });
    },
    onSuccess: () => {
      setOlder([]);
      void queryClient.invalidateQueries({ queryKey: ['hub-feed'] });
    },
  });

  // --- @mention autocomplete -------------------------------------------------
  // While the text before the caret ends in `@partial`, show matching members.
  function onDraftChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setDraft(value);
    const caret = e.target.selectionStart ?? value.length;
    const m = value.slice(0, caret).match(/@([A-Za-z0-9._-]*)$/);
    setMentionQuery(m ? (m[1] ?? '') : null);
  }

  const mentionMatches = mentionQuery === null
    ? []
    : (users.data ?? [])
        .filter((u) => {
          const q = mentionQuery.toLowerCase();
          return [
            u.displayName.replace(/\s+/g, ''),
            u.displayName.split(/\s+/)[0] ?? '',
          ].some((n) => n.toLowerCase().startsWith(q));
        })
        .slice(0, 8);

  function insertMention(u: UserRow) {
    const token = mentionToken(u);
    const el = inputRef.current;
    const caret = el?.selectionStart ?? draft.length;
    const head = draft.slice(0, caret).replace(/@[A-Za-z0-9._-]*$/, `@${token} `);
    setDraft(head + draft.slice(caret));
    setMentionQuery(null);
    // Restore focus + caret after React re-renders the input.
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(head.length, head.length);
    });
  }
  // ----------------------------------------------------------------------------

  const send = useMutation({
    mutationFn: async ({ text, attachment }: { text: string; attachment: HubAttachment | null }) => {
      if (text.toLowerCase().startsWith('@oppy ')) {
        setAsking(true);
        try {
          await apiFetch('/v1/hub/ask', {
            method: 'POST',
            body: JSON.stringify({
              question: text.slice(6).trim(),
              ...(attachment ? { attachment } : {}),
            }),
          });
        } finally {
          setAsking(false);
        }
      } else {
        await apiFetch('/v1/hub/messages', {
          method: 'POST',
          body: JSON.stringify({
            ...(text ? { body: text } : {}),
            ...(attachment ? { attachment } : {}),
          }),
        });
      }
    },
    onSuccess: () => {
      setDraft('');
      setPendingAttachment(null);
      void queryClient.invalidateQueries({ queryKey: ['hub-feed'] });
    },
  });

  // --- Attachments -----------------------------------------------------------
  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { setComposerError('File too large — max 200 MB.'); return; }
    setComposerError(null);
    setUploading(true);
    try {
      setPendingAttachment(await uploadHubFile(file, file.name));
    } catch (err) {
      setComposerError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  // --- Voice notes -----------------------------------------------------------
  function stopRecTimer() {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
  }

  async function startRecording() {
    setComposerError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setComposerError('Voice recording is not supported in this browser.');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setComposerError('Microphone access denied — allow mic permission to record voice notes.');
      return;
    }
    const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4', '']
      .find((m) => m === '' || MediaRecorder.isTypeSupported(m)) ?? '';
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    recChunksRef.current = [];
    recCancelRef.current = false;
    rec.ondataavailable = (e) => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      stopRecTimer();
      if (recCancelRef.current) { setRecState('idle'); return; }
      const durationSec = Math.max(1, Math.round((Date.now() - recStartRef.current) / 1000));
      const blob = new Blob(recChunksRef.current, { type: rec.mimeType || 'audio/webm' });
      setRecPreview({ blob, durationSec, url: URL.createObjectURL(blob) });
      setRecState('preview');
    };
    recStartRef.current = Date.now();
    recorderRef.current = rec;
    rec.start(1000);
    setRecSeconds(0);
    recTimerRef.current = setInterval(() => {
      const s = Math.floor((Date.now() - recStartRef.current) / 1000);
      setRecSeconds(s);
      if (s >= 300) stopRecording(); // 5-minute cap
    }, 250);
    setRecState('recording');
  }

  function stopRecording() {
    stopRecTimer();
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (rec && rec.state !== 'inactive') rec.stop();
  }

  function cancelRecording() {
    recCancelRef.current = true;
    stopRecording();
  }

  function discardPreview() {
    if (recPreview) URL.revokeObjectURL(recPreview.url);
    setRecPreview(null);
    setRecState('idle');
  }

  async function sendVoiceNote() {
    if (!recPreview || uploading) return;
    const ext = recPreview.blob.type.includes('ogg') ? 'ogg' : recPreview.blob.type.includes('mp4') ? 'm4a' : 'webm';
    setUploading(true);
    setComposerError(null);
    try {
      const meta = await uploadHubFile(recPreview.blob, `voice-note.${ext}`);
      send.mutate({
        text: draft.trim(),
        attachment: { ...meta, kind: 'audio', durationSec: recPreview.durationSec },
      });
      discardPreview();
    } catch (err) {
      setComposerError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  // Stop the mic if the page unmounts mid-recording.
  useEffect(() => () => {
    recCancelRef.current = true;
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    if (recTimerRef.current) clearInterval(recTimerRef.current);
  }, []);

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-4rem)]">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold text-white">Hub</h1>
          <p className="text-xs text-muted">Everything happening at 1Financial — activity, chat, and Oppy ✦</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden text-xs">
            {(['all', 'activity', 'chat'] as const).map((k) => (
              <button
                key={k}
                onClick={() => setKind(k)}
                className={`px-3 py-1.5 ${kind === k ? 'bg-accent text-accent-fg font-medium' : 'text-muted hover:text-white'}`}
              >
                {k === 'all' ? 'All' : k === 'activity' ? 'Activity' : 'Chat'}
              </button>
            ))}
          </div>
          <SearchableSelect
            value={person}
            onChange={setPerson}
            placeholder="Everyone"
            options={(users.data ?? []).map((u) => ({ value: u.id, label: u.displayName }))}
            className="w-full bg-bg-base border border-border rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-accent"
            wrapperClassName="w-40"
          />
          {canDo('hub.clear') && (
            <button
              onClick={() => {
                if (window.confirm('Clear the Hub chat messages you are authorized to clear? Activity entries stay.')) {
                  clearChat.mutate();
                }
              }}
              disabled={clearChat.isPending}
              className="text-xs px-3 py-1.5 rounded-lg border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
            >
              {clearChat.isPending ? 'Clearing…' : 'Clear chat'}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-bg-panel border border-border rounded-xl p-4 space-y-2 min-h-0">
        <div className="text-center">
          <button onClick={() => void loadEarlier()} className="text-xs text-accent hover:underline">Load earlier</button>
        </div>
        {feed.isLoading && <p className="text-sm text-muted text-center py-8">Loading the stream…</p>}
        {feed.error && <p className="text-sm text-rose-300 text-center py-8">{(feed.error as Error).message}</p>}
        {items.map((item) =>
          item.kind === 'activity' ? (
            <div key={item.id} className="flex items-start gap-3 px-2 py-1.5">
              <span className="text-base leading-6 w-6 text-center shrink-0">{ACTION_ICON[item.action] ?? '•'}</span>
              <div className="min-w-0">
                <p className="text-sm text-white">
                  {item.summary}
                  {entityLink(item) && (
                    <> <Link to={entityLink(item)!} className="text-xs text-accent hover:underline ml-1">view →</Link></>
                  )}
                </p>
                <p className="text-xs text-muted">{fmtTime(item.createdAt)}</p>
              </div>
            </div>
          ) : item.authorKind === 'SYSTEM' ? (
            <div key={item.id} className="text-center py-1">
              <p className="inline-block text-xs text-muted bg-bg-card border border-border rounded-lg px-3 py-1.5 whitespace-pre-line">{item.body}</p>
              <p className="text-[10px] text-muted mt-0.5">{fmtTime(item.createdAt)}</p>
            </div>
          ) : (
            <div key={item.id} className={`flex ${item.authorKind === 'OPPY' ? 'justify-start' : 'justify-end'}`}>
              <div
                className={`max-w-[75%] rounded-xl px-3 py-2 ${
                  item.authorKind === 'OPPY'
                    ? 'bg-bg-card border border-accent/40'
                    : 'bg-accent/15 border border-accent/20'
                }`}
              >
                <p className={`text-xs font-medium mb-0.5 ${item.authorKind === 'OPPY' ? 'text-accent' : 'text-muted'}`}>
                  {item.authorKind === 'OPPY' ? '✦ Oppy' : item.authorName}
                </p>
                {item.body && (
                  <p className="text-sm text-white whitespace-pre-wrap break-words">{renderBody(item.body)}</p>
                )}
                {item.attachment && <AttachmentView att={item.attachment} />}
                <p className="text-[10px] text-muted mt-0.5">{fmtTime(item.createdAt)}</p>
              </div>
            </div>
          ),
        )}
        {asking && (
          <div className="flex justify-start">
            <div className="bg-bg-card border border-accent/40 rounded-xl px-3 py-2">
              <p className="text-xs font-medium text-accent mb-0.5">✦ Oppy</p>
              <p className="text-sm text-muted">thinking…</p>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {composerError && <p className="mt-2 text-xs text-rose-400">{composerError}</p>}

      {pendingAttachment && (
        <div className="mt-2 flex items-center gap-2 w-fit text-xs text-muted bg-bg-panel border border-border rounded-lg px-3 py-1.5">
          <span className="text-white truncate max-w-[220px]">📎 {pendingAttachment.name}</span>
          <span className="shrink-0">{fmtSize(pendingAttachment.size)}</span>
          <button type="button" onClick={() => setPendingAttachment(null)} className="text-rose-400 hover:text-rose-300">✕</button>
        </div>
      )}
      {uploading && recState === 'idle' && <p className="mt-2 text-xs text-muted">Uploading attachment…</p>}

      {recState === 'recording' && (
        <div className="mt-2 flex items-center gap-3 bg-bg-panel border border-rose-500/40 rounded-xl px-4 py-2">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse shrink-0" />
          <span className="text-sm text-rose-300 font-mono">{fmtDuration(recSeconds)}</span>
          <span className="text-xs text-muted">Recording… (max 5:00)</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={cancelRecording} className="text-xs text-muted hover:text-white px-2 py-1">Cancel</button>
            <button type="button" onClick={stopRecording} className="text-xs bg-rose-600 hover:bg-rose-500 text-oncolor px-3 py-1 rounded-lg">Stop</button>
          </div>
        </div>
      )}
      {recState === 'preview' && recPreview && (
        <div className="mt-2 flex items-center gap-3 flex-wrap bg-bg-panel border border-border rounded-xl px-4 py-2">
          <audio controls src={recPreview.url} className="h-8 max-w-full" />
          <span className="text-xs text-muted">{fmtDuration(recPreview.durationSec)}</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={discardPreview} className="text-xs text-muted hover:text-white px-2 py-1">Discard</button>
            <button
              type="button"
              onClick={() => void sendVoiceNote()}
              disabled={uploading || send.isPending}
              className="text-xs bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg font-medium px-3 py-1 rounded-lg"
            >
              {uploading ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setMentionQuery(null);
          const text = draft.trim();
          if ((text || pendingAttachment) && !send.isPending) send.mutate({ text, attachment: pendingAttachment });
        }}
        className="mt-3 relative flex gap-2"
      >
        {mentionMatches.length > 0 && (
          <div className="absolute bottom-full left-0 mb-1 w-72 bg-bg-panel border border-border rounded-xl overflow-hidden shadow-lg z-10">
            {mentionMatches.map((u) => (
              <button
                key={u.id}
                type="button"
                // onMouseDown so the click lands before the input loses focus.
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(u);
                }}
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-bg-hover flex items-center justify-between"
              >
                <span>{u.displayName}</span>
                <span className="text-xs text-accent">@{mentionToken(u)}</span>
              </button>
            ))}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*,audio/*,.pdf,.doc,.docx,.xlsx,.xls,.txt"
          onChange={(e) => void onFilePicked(e)}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || recState === 'recording'}
          title="Attach a file"
          className="shrink-0 border border-border hover:border-accent text-muted hover:text-white disabled:opacity-50 px-3 rounded-xl"
        >
          📎
        </button>
        <button
          type="button"
          onClick={() => void startRecording()}
          disabled={uploading || recState !== 'idle'}
          title="Record a voice note"
          className="shrink-0 border border-border hover:border-accent text-muted hover:text-white disabled:opacity-50 px-3 rounded-xl"
        >
          🎙
        </button>
        <input
          ref={inputRef}
          value={draft}
          onChange={onDraftChange}
          onBlur={() => setMentionQuery(null)}
          placeholder="Say something to the team — mention with @name, or ask Oppy with @oppy <question>"
          className="flex-1 bg-bg-panel border border-border rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-accent"
        />
        <button
          type="submit"
          disabled={send.isPending || (!draft.trim() && !pendingAttachment)}
          className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-5 rounded-xl"
        >
          Send
        </button>
      </form>
    </div>
  );
}
